import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import CTABanner from "@/components/CTABanner";
import articles, { categories } from "./blog/articles";

// Static article index. This page used to fetch /api/blog, which returned 500
// (missing Firestore index) against a collection holding zero documents, with
// no working admin API to publish through — every /api/admin/* route 404s.
// Articles now live in src/pages/blog/articles.js and are prerendered, so the
// content cannot be taken down by a backend fault.

const ORIGIN = "https://aussieinspections.com.au";

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filtered =
    selectedCategory === "all"
      ? articles
      : articles.filter((a) => a.category === selectedCategory);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Aussie Inspections Guides",
    "description":
      "Guides to building, pest, pool and plumbing inspections in Darwin and the Northern Territory.",
    "url": `${ORIGIN}/blog`,
    "publisher": {
      "@type": "Organization",
      "name": "Aussie Inspections",
      "url": ORIGIN,
      "logo": { "@type": "ImageObject", "url": `${ORIGIN}/logo.png` },
    },
    "blogPost": articles.map((a) => ({
      "@type": "BlogPosting",
      "headline": a.title,
      "description": a.description,
      "datePublished": a.publishedAt,
      "dateModified": a.updatedAt,
      "url": `${ORIGIN}/blog/${a.slug}`,
      "author": { "@type": "Organization", "name": "Aussie Inspections" },
    })),
  };

  return (
    <div>
      <SEO
        title="Inspection Guides for Darwin & the Top End"
        description="Plain-English guides to building inspections, NT pool compliance, termites in the Top End, building status reports and what to check before you buy in Darwin."
        keywords="building inspection guides Darwin, NT pool compliance, Darwin termites, building status report NT, property inspection advice"
        url="/blog"
        schema={schema}
      />

      <CTABanner />

      <section className="bg-primary text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-chivo text-5xl md:text-6xl font-black tracking-tight leading-none mb-4">
              Inspection Guides
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Straight answers about inspecting, buying and owning property in Darwin and the Top End
            </p>
          </motion.div>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory("all")}
              data-testid="category-all"
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                selectedCategory === "all"
                  ? "bg-primary text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              All Guides
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                data-testid={`category-${category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  selectedCategory === category
                    ? "bg-primary text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((a, index) => (
              <motion.article
                key={a.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
                className="flex flex-col rounded-xl border border-slate-200 bg-white p-7 hover:border-primary transition-colors"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-3">
                  {a.category}
                </p>
                <h2 className="font-chivo text-xl font-bold text-slate-900 mb-3 leading-snug tracking-tight">
                  <Link to={`/blog/${a.slug}`} className="hover:text-primary transition-colors">
                    {a.title}
                  </Link>
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6 flex-1">{a.description}</p>
                <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-4 w-4" />
                    <time dateTime={a.publishedAt}>{formatDate(a.publishedAt)}</time>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="h-4 w-4" />
                    {a.readTime}
                  </span>
                </div>
                <Link
                  to={`/blog/${a.slug}`}
                  className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline"
                >
                  Read guide <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
